import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGameData } from "./GameContext";
import { Scatter } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import Navbar from "../components/Navbar";

ChartJS.register(CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend, ChartDataLabels);

interface HoroscopeListItem {
    Header: string;
    SubHeader1: string;
    SubHeader2: string;
    SubHeader3: string;
    SubHeader1Score: number;
    SubHeader2Score: number;
    SubHeader3Score: number;
    Puan: number;
    subVeri: {
        planet: string;
        horoscope: string;
        highHeader1Score: number;
        highHeader2Score: number;
        highHeader3Score: number;
    };
}

const HoroscopeListDetail: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const { gameData } = useGameData();
    const { t } = useLanguage();
    const sessions = gameData["game4"] || [];
    const session = sessions.find((s) => s.userId === userId);

    if (!session || !session.HoroscopeList) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 text-center">{t("user_details")}</h1>
                <p className="text-gray-600 mb-6 text-center">{t("no_data")}</p>
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate("/users/game4")}
                    className="bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200"
                >
                    {t("back_to_users")}
                </motion.button>
            </div>
        );
    }

    let horoscopeList: HoroscopeListItem[] = [];
    try {
        horoscopeList = JSON.parse(session.HoroscopeList);
    } catch (error) {
        console.error("HoroscopeList parse error:", error);
    }

    // Nokta Grafik Verisi
    const chartData = {
        datasets: [
            {
                label: t("avg_score"),
                data: horoscopeList.map((item, index) => ({ x: index, y: item.Puan })),
                backgroundColor: "rgba(0, 172, 193, 0.7)",
                pointRadius: 6,
                pointHoverRadius: 8,
            },
            {
                label: t("feature_1"),
                data: horoscopeList.map((item, index) => ({ x: index, y: item.SubHeader1Score })),
                backgroundColor: "rgba(255, 152, 0, 0.7)",
                pointRadius: 6,
                pointHoverRadius: 8,
            },
            {
                label: t("feature_2"),
                data: horoscopeList.map((item, index) => ({ x: index, y: item.SubHeader2Score })),
                backgroundColor: "rgba(76, 175, 80, 0.7)",
                pointRadius: 6,
                pointHoverRadius: 8,
            },
            {
                label: t("feature_3"),
                data: horoscopeList.map((item, index) => ({ x: index, y: item.SubHeader3Score })),
                backgroundColor: "rgba(255, 87, 34, 0.7)",
                pointRadius: 6,
                pointHoverRadius: 8,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top" as const,
                labels: { font: { size: 10, family: "Arial" }, padding: 10 },
                align: "center" as const,
            },
            title: {
                display: true,
                text: `${t("user")} - ${t("horoscope_list")}`, // Removed userId from title
                font: { size: 16, weight: "bold" as const, family: "Arial" },
                color: "#1f2937",
                padding: { top: 10, bottom: 20 },
            },
            datalabels: {
                display: (context: any) => context.chart.width > 768,
                color: "#333",
                font: { weight: "bold" as const, size: 10 },
                formatter: (value: { x: number; y: number }) => value.y.toFixed(2),
                anchor: "center" as const,
                align: "top" as const,
            },
            tooltip: {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                titleFont: { size: 12 },
                bodyFont: { size: 10 },
                callbacks: {
                    label: (context: any) => {
                        const datasetLabel = context.dataset.label || "";
                        const value = context.raw.y;
                        const dataIndex = context.dataIndex;
                        const datasetIndex = context.datasetIndex;

                        let personality;
                        const header = horoscopeList[dataIndex].Header;
                        if (datasetIndex === 0) {
                            personality = t("avg_score");
                        } else if (datasetIndex === 1) {
                            personality = horoscopeList[dataIndex].SubHeader1;
                        } else if (datasetIndex === 2) {
                            personality = horoscopeList[dataIndex].SubHeader2;
                        } else if (datasetIndex === 3) {
                            personality = horoscopeList[dataIndex].SubHeader3;
                        }

                        return `${personality} - ${header}: ${value.toFixed(2)}`;
                    },
                },
            },
        },
        scales: {
            x: {
                type: "linear" as const,
                ticks: {
                    stepSize: 1,
                    callback: function (tickValue: string | number) {
                        return horoscopeList[Number(tickValue)]?.Header.slice(0, 10) || "";
                    },
                    font: { size: 10 },
                    maxRotation: 45,
                    minRotation: 45,
                },
                title: { display: true, text: t("personalities"), font: { size: 12 } },
                grid: { color: "rgba(0, 0, 0, 0.05)" },
            },
            y: {
                beginAtZero: true,
                title: { display: true, text: t("scores"), font: { size: 12 } },
                grid: { color: "rgba(0, 0, 0, 0.05)" },
                ticks: { font: { size: 10 } },
            },
        },
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Navbar />
            <main className="mt-16 sm:mt-36 mb-12 flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-6 sm:mb-8 flex flex-col sm:flex-row items-center justify-between gap-4"
                >
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 text-center group relative">
                        <span>{t("horoscope_list_details")}</span>
                    </h2>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate("/users/game4")}
                        className="bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200 w-full sm:w-auto"
                    >
                        {t("back_to_users")}
                    </motion.button>
                </motion.div>

                {/* Grafik */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8 h-64 sm:h-96"
                >
                    <Scatter data={chartData} options={chartOptions} />
                </motion.div>

                {/* Detaylı Tablo */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="bg-white rounded-xl shadow-lg p-4 sm:p-6 overflow-x-auto"
                >
                    <table className="w-full text-xs sm:text-sm text-gray-600">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium text-gray-700 whitespace-nowrap">{t("personality")}</th>
                                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium text-gray-700 whitespace-nowrap">{t("feature_1")}</th>
                                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium text-gray-700 whitespace-nowrap">{t("feature_2")}</th>
                                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium text-gray-700 whitespace-nowrap">{t("feature_3")}</th>
                                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium text-gray-700 whitespace-nowrap">{t("avg_score")}</th>
                                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium text-gray-700 whitespace-nowrap">{t("planet")}</th>
                                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium text-gray-700 whitespace-nowrap">{t("horoscope")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {horoscopeList.map((item, index) => (
                                <tr key={index} className="border-b hover:bg-gray-50">
                                    <td className="py-2 px-2 sm:py-3 sm:px-4 whitespace-nowrap">{item.Header}</td>
                                    <td className="py-2 px-2 sm:py-3 sm:px-4 whitespace-nowrap">{`${item.SubHeader1.charAt(0).toUpperCase() + item.SubHeader1.slice(1).toLowerCase()} (${item.SubHeader1Score})`}</td>
                                    <td className="py-2 px-2 sm:py-3 sm:px-4 whitespace-nowrap">{`${item.SubHeader2.charAt(0).toUpperCase() + item.SubHeader2.slice(1).toLowerCase()} (${item.SubHeader2Score})`}</td>
                                    <td className="py-2 px-2 sm:py-3 sm:px-4 whitespace-nowrap">{`${item.SubHeader3.charAt(0).toUpperCase() + item.SubHeader3.slice(1).toLowerCase()} (${item.SubHeader3Score})`}</td>
                                    <td className="py-2 px-2 sm:py-3 sm:px-4 whitespace-nowrap">{item.Puan.toFixed(2)}</td>
                                    <td className="py-2 px-2 sm:py-3 sm:px-4 whitespace-nowrap">{item.subVeri.planet.charAt(0).toUpperCase() + item.subVeri.planet.slice(1).toLowerCase()}</td>
                                    <td className="py-2 px-2 sm:py-3 sm:px-4 whitespace-nowrap">{item.subVeri.horoscope.charAt(0).toUpperCase() + item.subVeri.horoscope.slice(1).toLowerCase()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </motion.div>
            </main>

            <footer className="bg-gray-800 py-4">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <p className="text-gray-300 text-xs sm:text-sm">{t("footer", { year: new Date().getFullYear() })}</p>
                </div>
            </footer>
        </div>
    );
};

export default HoroscopeListDetail;